import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

/**
 * Repayment calculator — vanilla JS, no external deps.
 * Used on every finance category page.
 */
export default function RepaymentCalculator({ defaultRate = 6.99, defaultTerm = 25, defaultAmount = 500000, onApply }) {
  const [amount, setAmount] = useState(defaultAmount);
  const [rate, setRate] = useState(defaultRate);
  const [term, setTerm] = useState(defaultTerm);

  const { monthly, total, interest } = useMemo(() => {
    const P = Number(amount) || 0;
    const r = (Number(rate) || 0) / 100 / 12;
    const n = (Number(term) || 0) * 12;
    if (!P || !n) return { monthly: 0, total: 0, interest: 0 };
    const m = r === 0 ? P / n : (P * r) / (1 - Math.pow(1 + r, -n));
    const t = m * n;
    return { monthly: m, total: t, interest: t - P };
  }, [amount, rate, term]);

  const fmt = (v) =>
    new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(v);

  return (
    <div data-testid="repayment-calculator" className="bg-[#0a0a0a] border border-zinc-800 rounded-2xl p-6 sm:p-8 space-y-5">
      <h3 className="font-bebas text-2xl sm:text-3xl tracking-wide">Repayment calculator</h3>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <label className="space-y-1.5 block">
          <span className="text-xs uppercase tracking-wider text-zinc-500">Loan amount (AUD)</span>
          <Input
            type="number"
            value={amount}
            min={0}
            onChange={(e) => setAmount(e.target.value)}
            className="bg-[#111] border-zinc-800 text-white h-11 focus-visible:ring-[#E50914] focus-visible:border-[#E50914]"
          />
        </label>
        <label className="space-y-1.5 block">
          <span className="text-xs uppercase tracking-wider text-zinc-500">Rate (% p.a.)</span>
          <Input
            type="number"
            step="0.01"
            value={rate}
            min={0}
            onChange={(e) => setRate(e.target.value)}
            className="bg-[#111] border-zinc-800 text-white h-11 focus-visible:ring-[#E50914] focus-visible:border-[#E50914]"
          />
        </label>
        <label className="space-y-1.5 block">
          <span className="text-xs uppercase tracking-wider text-zinc-500">Term (years)</span>
          <Input
            type="number"
            value={term}
            min={1}
            max={30}
            onChange={(e) => setTerm(e.target.value)}
            className="bg-[#111] border-zinc-800 text-white h-11 focus-visible:ring-[#E50914] focus-visible:border-[#E50914]"
          />
        </label>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Stat label="Monthly" value={fmt(monthly)} highlight />
        <Stat label="Total interest" value={fmt(interest)} />
        <Stat label="Total repaid" value={fmt(total)} />
      </div>

      <Button
        type="button"
        onClick={() => onApply?.({ loan_amount: fmt(amount) })}
        className="w-full h-12 bg-[#E50914] hover:bg-[#F6121D] rounded-full font-semibold"
      >
        Get an exact quote
      </Button>

      <p className="text-[11px] text-zinc-600 leading-relaxed">
        Indicative only. Actual repayments depend on lender, credit profile, fees and product. Use this as a starting point — we'll provide a precise comparison on enquiry.
      </p>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`rounded-xl border p-3 sm:p-4 ${highlight ? "bg-[#1a0507] border-[#E50914]/30" : "bg-[#111] border-zinc-800"}`}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`mt-1 font-bebas text-xl sm:text-2xl tracking-wide ${highlight ? "text-[#E50914]" : "text-white"}`}>
        {value}
      </div>
    </div>
  );
}
